import { T, useGT } from 'gt-react';
import { TooltipInfoIcon } from '@/components/common/TooltipInfoIcon';
import { cn } from '@/lib/utils';
import { OverridableValue } from './inline-overrides/OverridableValue';
import { useInlineOverrideField } from './inline-overrides/InlineOverridesContext';

/**
 * Every panel on this page is the same shape: a heading and a column of
 * label/value pairs, open on the pane. One component, so the groups line up
 * across the page instead of each drifting to its own spacing.
 */
export function DetailPanel({
  title,
  children,
  className,
  footer,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  footer?: React.ReactNode;
}) {
  return (
    <section className={cn('min-w-0', className)}>
      <h3 className="mb-1 text-xs font-medium text-foreground">{title}</h3>
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
        {children}
      </dl>
      {footer}
    </section>
  );
}

/**
 * A dash, not a zero, when the value is absent.
 *
 * On an inventory screen the difference is the whole message: 0 on hand is a
 * stockout, and no snapshot row is "we have never counted this here". Printing
 * one as the other would have a buyer act on a number nobody measured.
 */
export function Field({
  label,
  value,
  hint,
  emphasis,
  overrideId,
}: {
  label: React.ReactNode;
  value: string | number | null | undefined;
  /**
   * How the value is calculated and where its data comes from. Shown behind an
   * info icon beside the label rather than as a hover title, so it reaches
   * keyboard and touch users too — planners asked for every number here to
   * explain itself.
   */
  hint?: string;
  emphasis?: boolean;
  /**
   * The inline override field this value can be pinned through. Where the page
   * has resolved to one location the value is marked and opens its editor;
   * across a scope of several it stays a plain value.
   */
  overrideId?: string;
}) {
  const gt = useGT();
  const overridable = useInlineOverrideField(overrideId);
  const shown =
    value === null || value === undefined || value === ''
      ? '—'
      : typeof value === 'number'
        ? Math.round(value).toLocaleString()
        : value;
  return (
    <>
      <dt className="flex min-w-0 items-center gap-1 text-muted-foreground">
        <span className="truncate">{label}</span>
        {hint ? (
          <TooltipInfoIcon
            description={hint}
            ariaLabel={gt('How this is calculated', {
              context: 'forecast detail field help button label',
            })}
          />
        ) : null}
      </dt>
      <dd
        className={cn(
          'min-w-0 text-right tabular-nums break-words',
          emphasis && 'font-semibold',
          shown === '—' && 'text-muted-foreground/60',
        )}
      >
        {overridable && shown !== '—' ? (
          <OverridableValue
            field={overridable.field}
            canEdit={overridable.canEdit}
          >
            {shown}
          </OverridableValue>
        ) : (
          shown
        )}
      </dd>
    </>
  );
}

/** A titled column of fields; the Demand behavior section holds four. */
export function FieldGroup({
  title,
  children,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="min-w-0">
      <h3 className="mb-1 text-xs font-medium text-foreground">{title}</h3>
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
        {children}
      </dl>
    </section>
  );
}

/**
 * Groups side by side, told apart by a hairline between them rather than a
 * card around each: on a wide screen they read as one row of related facts,
 * and below that they wrap into columns that the gap alone separates.
 */
export function DividedGroups({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid gap-x-6 gap-y-4 md:grid-cols-2 xl:auto-cols-fr xl:grid-flow-col xl:grid-cols-none xl:gap-x-0 xl:divide-x xl:[&>*]:px-5 xl:[&>*:first-child]:pl-0 xl:[&>*:last-child]:pr-0">
      {children}
    </div>
  );
}

export function PanelNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="col-span-2 pt-1 text-xs leading-snug text-muted-foreground">
      {children}
    </p>
  );
}

export function NotInRunNote() {
  return (
    <PanelNote>
      <T context="forecast detail, series absent from the run">
        This run holds no forecast for this item at this location. The stock and
        policy below are current either way.
      </T>
    </PanelNote>
  );
}
